import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check, ChevronDown } from "lucide-react";
import { SUPPORTED_LANGS } from "@/i18n";

interface LanguageSwitcherProps {
  variant?: "light" | "dark";
  /** Anchor side for the dropdown. Use "left" when the button sits at the
   *  left edge of a container (e.g. mobile overlay) so the dropdown opens
   *  toward the right and stays on-screen. Defaults to "right" for the
   *  desktop Navbar where the switcher lives on the right edge. */
  align?: "left" | "right";
}

const LanguageSwitcher = ({ variant = "dark", align = "right" }: LanguageSwitcherProps) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current =
    SUPPORTED_LANGS.find((l) => l.code === i18n.language) || SUPPORTED_LANGS[0];

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const btnColor =
    variant === "light"
      ? "text-white/80 hover:text-white"
      : "text-stone-600 hover:text-stone-900";

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 text-sm font-medium tracking-wide transition-colors duration-200 cursor-pointer ${btnColor}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Current language: ${current.label}. Click to change.`}
      >
        <Globe size={16} className="shrink-0" />
        <span className="whitespace-nowrap">{current.label}</span>
        <ChevronDown
          size={14}
          className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <ul
          role="listbox"
          className={`absolute mt-2 w-48 bg-white border border-stone-200 rounded-lg shadow-lg py-2 z-50 ${
            align === "left" ? "left-0" : "right-0"
          }`}
        >
          {SUPPORTED_LANGS.map((l) => {
            const isActive = l.code === i18n.language;
            return (
              <li key={l.code}>
                <button
                  type="button"
                  onClick={() => {
                    i18n.changeLanguage(l.code);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-4 py-2 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    isActive
                      ? "text-amber-700 font-semibold bg-amber-50"
                      : "text-stone-700 hover:bg-stone-50"
                  }`}
                  role="option"
                  aria-selected={isActive}
                  lang={l.code}
                  dir={l.dir}
                >
                  <span>{l.label}</span>
                  {isActive && <Check size={14} />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
